import { gql } from '@apollo/client';

import { NONE_TEXT } from '../components/displayFields/AxleInfoField';
import { IAddressWithLatLngFragment } from '../fetching/sharedSchemas/addresses';
import { DefiniteComponent } from './reactTypes';

type Address = {
    address1: string;
    address2: string | null;
    city: string;
    state: string;
    zip_code: string;
};

export const BasicAddressFragment = gql`
    fragment BasicAddressFragment on Address {
        id
        line1
        line2
        city
        state
        zipCode
    }
`;

export interface IBasicAddress {
    id: string;
    line1: string;
    line2: string;
    city: string;
    state: string;
    zipCode: string;
}

export type AddressInput = {
    line1: string;
    line2: string;
    city: string;
    state: string;
    zipCode: string;
};

export const BasicAddressWithCoordinatesFragment = gql`
    fragment BasicAddressWithCoordinatesFragment on Address {
        id
        line1
        line2
        city
        state
        zipCode
        coordinates {
            latitude
            longitude
        }
    }
`;

export interface IBasicAddressWithCoordinates extends IBasicAddress {
    coordinates: {
        latitude: string;
        longitude: string;
    } | null;
}

export const BasicAddressWithTimezoneFragment = gql`
    fragment BasicAddressWithTimezoneFragment on Address {
        id
        line1
        line2
        city
        state
        zipCode
        timezone
    }
`;

export interface IBasicAddressWithTimezone extends IBasicAddress {
    timezone: string;
}

// TODO: Stop using address UUIDs and exclusively use address objects.
// https://linear.app/axle-health/issue/AXL-1528/stop-using-address-uuids-and-exclusively-use-address-objects
export const EMPTY_ADDRESS: IBasicAddress = {
    id: '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    zipCode: '',
};

export const basicAddressToAddress = ({
    line1,
    line2,
    city,
    state,
    zipCode,
}: IBasicAddress): Address => {
    return {
        address1: line1,
        address2: line2,
        city: city,
        state: state,
        zip_code: zipCode,
    };
};

// IAddressWithLatLngFragment
export const addressWithLatLngFragmentToAddress = (
    address: IAddressWithLatLngFragment,
): Address => {
    return {
        address1: address.line1,
        address2: address.line2,
        city: address.city,
        state: address.state,
        zip_code: address.zipCode,
    };
};

export interface IBasicAddressWithAddressNotLine {
    id: string;
    address1: string;
    address2: string | null;
    city: string;
    state: string;
    zipCode: string;
}

export const addressZipCamelToSnake = (address: IBasicAddressWithAddressNotLine): Address => {
    return {
        address1: address.address1,
        address2: address.address2,
        city: address.city,
        state: address.state,
        zip_code: address.zipCode,
    };
};

export const addressInTwoLinesLine1 = (address: Address): string => {
    const { address1, address2 } = address;
    return address2 ? `${address1}, ${address2}` : address1;
};

export const addressInTwoLinesLine2 = (address: Address): string => {
    const { city, state, zip_code } = address;
    return `${city}, ${state} ${zip_code}`;
};

export const addressInOneLine = (address: Address | null): string =>
    address === null
        ? NONE_TEXT
        : `${addressInTwoLinesLine1(address)}, ${addressInTwoLinesLine2(address)}`;

// If you just embed this in JSX, the newline will not be rendered.
//  You might want to use the <AddressInTwoLines/> component below instead.
export const addressInTwoLines = (address: Address | null): string =>
    address === null
        ? NONE_TEXT
        : `${addressInTwoLinesLine1(address)}\n${addressInTwoLinesLine2(address)}`;

export const AddressInTwoLines = ({
    address,
    shouldWrap,
}: {
    address: Address | null;
    shouldWrap?: boolean;
}): DefiniteComponent => {
    //                        ↓ look! a little guy ↓
    if (address === null) return <>{'------'}</>;
    return (
        <span
            style={{
                whiteSpace: shouldWrap ? 'pre-line' : 'nowrap',
            }}
        >
            {addressInTwoLinesLine1(address)}
            <br />
            {addressInTwoLinesLine2(address)}
        </span>
    );
};
